import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(t("auth_page.welcome", { name: u.name }));
      const home = u.role === "customer" ? "/my-account" : u.role === "provider" ? "/portal" : "/admin";
      nav(home, { replace: true });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-appBg px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/"><Logo className="h-10" /></Link>
        </div>
        <div className="bg-white rounded-2xl p-8 ring-1 ring-slate-200 shadow-sm">
          <h1 className="font-heading text-2xl font-bold text-[#1B3A6B]">{t("auth_page.login_title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("auth_page.login_subtitle")}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">{t("auth_page.email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="pwd">{t("auth_page.password")}</Label>
              <Input id="pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full font-semibold h-11" data-testid="login-submit">
              {busy ? t("auth_page.signing_in") : t("auth_page.sign_in")}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm">
            <Link to="/forgot-password" className="text-slate-500 hover:text-[#1B3A6B]" data-testid="login-forgot-link">{t("auth_page.forgot_password")}</Link>
          </p>
          <p className="mt-2 text-center text-sm text-slate-500">
            {t("auth_page.new_customer")} <Link to="/signup" className="text-[#1B3A6B] font-semibold">{t("auth_page.create_account")}</Link>
          </p>
        </div>
        <div className="mt-6 text-center text-xs text-slate-500">
          <Link to="/" className="hover:text-[#1B3A6B]">{t("auth_page.back_to_site")}</Link>
        </div>
      </div>
    </div>
  );
}
